import { getCardSizeSpec } from "./cardSizes";
import { appendItem, compactItems } from "./geometry";
import { LAYOUT_SCHEMA_VERSION } from "./grid";
import { GRID_COLUMNS } from "./layoutConfig";
import { createDefaultItems, sortLayoutObjects } from "./seed";
import { SignalsObjectType } from "../../SignalsModels";
import type {
    LayoutItem,
    LayoutObject,
    SignalsDashboardLayoutV1,
} from "./types";

const clamp = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max);

/**
 * Brings a saved card within its type's size limits and inside the grid. A
 * layout saved before the limits changed can hold a card the renderer would
 * resize on its own, which would leave the layout dirty at the first gesture.
 */
function fitToSizeLimits(item: LayoutItem, object: LayoutObject): LayoutItem {
    const spec = getCardSizeSpec(object);
    const width = clamp(
        item.width,
        spec.minimumWidth,
        Math.min(spec.maximumWidth ?? GRID_COLUMNS, GRID_COLUMNS),
    );
    const height = clamp(
        item.height,
        spec.minimumHeight,
        spec.maximumHeight ?? Number.POSITIVE_INFINITY,
    );
    const x = clamp(item.x, 0, GRID_COLUMNS - width);

    if (width === item.width && height === item.height && x === item.x) {
        return item;
    }
    return { ...item, x, width, height };
}

/**
 * Where a new small card fits beside the small cards on the bottom row, as
 * the server's `DashboardLayout.Append` places it. Undefined when that row
 * holds any other card or has no room.
 */
function findSmallCardSlot(
    items: readonly LayoutItem[],
    objectById: ReadonlyMap<string, LayoutObject>,
    width: number,
    height: number,
): { x: number; y: number } | undefined {
    if (items.length === 0) return undefined;

    const bottom = Math.max(...items.map((item) => item.y + item.height));
    const rowTop = bottom - height;
    const rowItems = items.filter(
        (item) => item.y < bottom && item.y + item.height > rowTop,
    );
    const rowIsSmallCards = rowItems.every(
        (item) =>
            item.y === rowTop &&
            item.height === height &&
            objectById.get(item.objectId)?.objectType ===
                SignalsObjectType.SmallCard,
    );
    if (!rowIsSmallCards) return undefined;

    const x = Math.max(...rowItems.map((item) => item.x + item.width));
    return x + width <= GRID_COLUMNS ? { x, y: rowTop } : undefined;
}

/**
 * Brings a stored layout into agreement with the dashboard's current cards,
 * which may have been added, deleted or hidden by permissions since it was
 * saved. Known cards keep their geometry within their size limits, missing
 * cards are dropped, and new cards are placed at the bottom.
 */
export function reconcileLayout(
    layout: SignalsDashboardLayoutV1,
    objects: readonly LayoutObject[],
): SignalsDashboardLayoutV1 {
    const sortedObjects = sortLayoutObjects(objects);
    const objectById = new Map(
        sortedObjects.map((object) => [object.id, object]),
    );
    const knownItems = layout.items.flatMap((item) => {
        const object = objectById.get(item.objectId);
        return object ? [fitToSizeLimits(item, object)] : [];
    });
    const itemsByObjectId = new Map(
        knownItems.map((item) => [item.objectId, item]),
    );

    const missingObjects = sortedObjects.filter(
        (object) => !itemsByObjectId.has(object.id),
    );

    if (
        missingObjects.length === 0 &&
        itemsByObjectId.size === layout.items.length
    ) {
        return {
            ...layout,
            schemaVersion: LAYOUT_SCHEMA_VERSION,
            columns: GRID_COLUMNS,
            items: compactItems(knownItems, GRID_COLUMNS),
        };
    }

    // Keep the known cards in their saved order so placement stays stable.
    let items: LayoutItem[] = sortedObjects
        .map((object) => itemsByObjectId.get(object.id))
        .filter((item): item is LayoutItem => item !== undefined);

    if (items.length === 0) {
        items = createDefaultItems(sortedObjects);
    } else {
        // In order, so the most recently added card ends up last.
        missingObjects.forEach((object) => {
            const spec = getCardSizeSpec(object);
            const slot =
                object.objectType === SignalsObjectType.SmallCard
                    ? findSmallCardSlot(
                          items,
                          objectById,
                          spec.defaultWidth,
                          spec.defaultHeight,
                      )
                    : undefined;
            if (slot) {
                items = compactItems(
                    [
                        ...items,
                        {
                            objectId: object.id,
                            ...slot,
                            width: spec.defaultWidth,
                            height: spec.defaultHeight,
                        },
                    ],
                    GRID_COLUMNS,
                );
                return;
            }
            items = appendItem(
                items,
                {
                    objectId: object.id,
                    x: 0,
                    y: 0,
                    width: spec.defaultWidth,
                    height: spec.defaultHeight,
                },
                GRID_COLUMNS,
            );
        });
    }

    return {
        schemaVersion: LAYOUT_SCHEMA_VERSION,
        columns: GRID_COLUMNS,
        items: compactItems(items, GRID_COLUMNS),
    };
}
